import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ActionPanel = ({ candidate, onStatusUpdate, onScheduleInterview, onSendMessage }) => {
  const [quickNote, setQuickNote] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(candidate?.status);

  const statusOptions = [
    { value: 'pending', label: 'Pending Review' },
    { value: 'under-review', label: 'Under Review' },
    { value: 'interview-ready', label: 'Interview Ready' },
    { value: 'interviewed', label: 'Interviewed' },
    { value: 'selected', label: 'Selected' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'on-hold', label: 'On Hold' }
  ];

  const pipelineStages = [
    { value: 'screening', label: 'Initial Screening' },
    { value: 'technical', label: 'Technical Round' },
    { value: 'hr-round', label: 'HR Round' },
    { value: 'final', label: 'Final Interview' },
    { value: 'offer', label: 'Offer Stage' }
  ];

  const handleStatusUpdate = () => {
    if (onStatusUpdate) {
      onStatusUpdate(selectedStatus);
    }
  };

  const handleAddNote = () => {
    if (quickNote?.trim()) {
      // Handle note addition logic
      setQuickNote('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Primary Actions */}
      <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
        <h3 className="font-semibold text-card-foreground mb-4">Quick Actions</h3>
        <div className="space-y-3">
          <Button
            variant="default"
            fullWidth
            iconName="Calendar"
            iconPosition="left"
            onClick={onScheduleInterview}
            className="justify-start"
          >
            Schedule Interview
          </Button>
          
          <Button
            variant="outline"
            fullWidth
            iconName="Send"
            iconPosition="left"
            onClick={onSendMessage}
            className="justify-start"
          >
            Send Message
          </Button>
          
          <Button
            variant="secondary"
            fullWidth
            iconName="UserPlus"
            iconPosition="left"
            className="justify-start"
          >
            Add to Pipeline
          </Button>
          
          <Button
            variant="ghost"
            fullWidth
            iconName="Download"
            iconPosition="left"
            className="justify-start"
          >
            Download CV
          </Button>
        </div>
      </div>
      {/* Status Management */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-card-foreground mb-4">Status Management</h3>
        <div className="space-y-4">
          <Select
            label="Application Status"
            options={statusOptions}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />
          
          <Button
            variant="outline"
            fullWidth
            iconName="RefreshCw"
            iconPosition="left"
            onClick={handleStatusUpdate}
            disabled={selectedStatus === candidate?.status}
          >
            Update Status
          </Button>
        </div>
      </div>
      {/* Pipeline Stage */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-card-foreground mb-4">Pipeline Stage</h3>
        <Select
          label="Current Stage"
          options={pipelineStages}
          value={candidate?.pipelineStage}
          onChange={() => {}}
        />
      </div>
      {/* Quick Notes */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-card-foreground mb-4">Quick Notes</h3>
        <div className="space-y-4">
          <textarea
            value={quickNote}
            onChange={(e) => setQuickNote(e?.target?.value)}
            placeholder="Add a quick note about this candidate..."
            className="w-full p-3 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={3}
          />
          <Button
            variant="outline"
            fullWidth
            iconName="Plus"
            iconPosition="left"
            onClick={handleAddNote}
            disabled={!quickNote?.trim()}
          >
            Add Note
          </Button>
        </div>

        {/* Recent Notes */}
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-medium text-card-foreground">Recent Notes</h4>
          {candidate?.recentNotes?.map((note, index) => (
            <div key={index} className="bg-muted/30 rounded-lg p-3">
              <div className="flex items-start justify-between mb-2">
                <span className="text-sm font-medium text-foreground">{note?.author}</span>
                <span className="text-xs text-muted-foreground">{note?.date}</span>
              </div>
              <p className="text-sm text-muted-foreground">{note?.content}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Candidate Score Card */}
      <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/10 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Performance Metrics</h3>
            <p className="text-sm text-muted-foreground">AI-powered insights</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-success mb-1">{candidate?.aiScore}%</div>
            <div className="text-xs text-muted-foreground">Overall Score</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent mb-1">{candidate?.ranking}</div>
            <div className="text-xs text-muted-foreground">Ranking</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary mb-1">{candidate?.matchPercentage}%</div>
            <div className="text-xs text-muted-foreground">Job Match</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-secondary mb-1">{candidate?.responseTime}</div>
            <div className="text-xs text-muted-foreground">Response Time</div>
          </div>
        </div>
      </div>
      {/* Additional Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-card-foreground mb-4">Additional Actions</h3>
        <div className="space-y-2">
          <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-muted rounded-lg transition-colors micro-feedback">
            <Icon name="Share" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Share Profile</span>
          </button>
          <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-muted rounded-lg transition-colors micro-feedback">
            <Icon name="Copy" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Copy Profile Link</span>
          </button>
          <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-muted rounded-lg transition-colors micro-feedback">
            <Icon name="Flag" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Flag for Review</span>
          </button>
          <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-destructive/10 text-destructive rounded-lg transition-colors micro-feedback">
            <Icon name="Trash2" size={16} />
            <span className="text-sm">Archive Candidate</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionPanel;